/**
 * MonthGrid — 7×6 monthly calendar with discreet per-day signal.
 *
 * Design intent (executive, low-noise):
 *   - Each cell shows the day number + at most ONE colored dot row of up to
 *     3 dots (one per event type). If more events exist, a "+N" badge.
 *   - Today is marked with an accent ring; days with high-severity events
 *     get a single small red dot in the corner — never a red background.
 *   - Click a cell → fires onSelectDate(YYYY-MM-DD).
 *
 * Props:
 *   - year, month: 0-indexed month (Jan=0)
 *   - events: full event array for the month (already filtered)
 *   - selectedDate?: ISO string of the currently focused day
 *   - onSelectDate(iso): callback
 *   - todayISO: ISO string of "today"
 */
import React, { useMemo } from "react";
import { TYPE_STYLE } from "@/components/calendar/EventBadge";

const WEEKDAYS = ["L", "M", "X", "J", "V", "S", "D"];

function startOfMonthGrid(year, month) {
  // Monday-first grid. JS getDay(): 0=Sun..6=Sat. Convert to Monday-first index 0..6.
  const first = new Date(year, month, 1);
  const isoDow = (first.getDay() + 6) % 7;  // Mon=0..Sun=6
  const start = new Date(year, month, 1 - isoDow);
  return start;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function MonthGrid({ year, month, events, selectedDate, onSelectDate, todayISO }) {
  const start = startOfMonthGrid(year, month);

  // Bucket events by ISO date
  const byDate = useMemo(() => {
    const map = {};
    for (const e of events) {
      (map[e.date] = map[e.date] || []).push(e);
    }
    return map;
  }, [events]);

  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const iso = isoDate(d);
    const inMonth = d.getMonth() === month;
    const evs = byDate[iso] || [];
    const types = Array.from(new Set(evs.map(e => e.type))).slice(0, 3);
    const overflow = Math.max(0, evs.length - types.length);
    const hasHigh = evs.some(e => e.severity === "high");
    const isToday = iso === todayISO;
    const isSelected = iso === selectedDate;
    cells.push({ d, iso, inMonth, types, overflow, evs, hasHigh, isToday, isSelected });
  }

  return (
    <div className="dv-card p-3 sm:p-4" data-testid="month-grid">
      {/* Weekday header */}
      <div className="grid grid-cols-7 mb-2">
        {WEEKDAYS.map((w, i) => (
          <div
            key={i}
            className={`text-[11px] uppercase tracking-widest text-stone-400 text-center pb-2 ${i >= 5 ? "text-stone-300" : ""}`}
          >
            {w}
          </div>
        ))}
      </div>
      {/* Day cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => (
          <button
            key={i}
            onClick={() => onSelectDate(c.iso)}
            data-testid={`day-${c.iso}`}
            className={`
              relative text-left p-2 rounded-md min-h-[68px] sm:min-h-[88px] transition-colors
              ${c.inMonth ? "bg-white" : "bg-stone-50/50 text-stone-400"}
              ${c.isSelected ? "ring-2 ring-[#9C5236]" : c.isToday ? "ring-1 ring-stone-900" : "hover:bg-stone-50"}
            `}
          >
            <div className="flex items-start justify-between">
              <span
                className={`text-sm ${c.isToday ? "font-semibold" : ""} ${c.inMonth ? "" : "text-stone-300"}`}
              >
                {c.d.getDate()}
              </span>
              {c.hasHigh && (
                <span
                  aria-label="High severity"
                  className="w-1.5 h-1.5 rounded-full bg-red-500 mt-1 mr-0.5"
                />
              )}
            </div>
            {/* Type dots */}
            {c.types.length > 0 && (
              <div className="absolute bottom-2 left-2 right-2 flex items-center gap-1 flex-wrap">
                {c.types.map(t => {
                  const style = TYPE_STYLE[t] || TYPE_STYLE.task;
                  return (
                    <span
                      key={t}
                      className={`w-1.5 h-1.5 rounded-full ${style.bg.replace("/10", "")}`}
                      style={
                        t === "payment" ? { background: "#9C5236" } :
                        t === "task" ? { background: "#78716c" } :
                        t === "milestone" ? { background: "#15803d" } :
                        t === "meeting" ? { background: "#0891b2" } :
                        { background: "#0f766e" }
                      }
                      data-testid={`day-dot-${c.iso}-${t}`}
                    />
                  );
                })}
                {c.overflow > 0 && (
                  <span className="text-[10px] text-stone-500">+{c.overflow}</span>
                )}
              </div>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
